import React, { useState, useEffect } from 'react';
import { PaginationControls } from '../../components/PaginationControls';
import PageHeader from '../../components/PageHeader';
import { stream_data } from '../../config/stream_data';
import { Offcanvas, Button } from 'react-bootstrap';

import Select from 'react-select';

const StudentProgramReport = () => {
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        total: 0,
        from: 0,
        to: 0
    });
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilterCanvas, setShowFilterCanvas] = useState(false);
    // const itemsPerPage = 10;

    // Filter States
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [programFilter, setProgramFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [degreeFilter, setDegreeFilter] = useState(null);
    const [studyingYearFilter, setStudyingYearFilter] = useState(null);

    // Dropdown Data States
    const [programOptions, setProgramOptions] = useState([]);
    const [studentOptions, setStudentOptions] = useState([]);
    const [degreeOptions, setDegreeOptions] = useState([]);
    const [studyingYearOptions, setStudyingYearOptions] = useState([]);

    const API_BASE_URL = process.env.REACT_APP_API_LMS;
    const API_TOKEN = process.env.REACT_APP_API_TOKEN;
    const instituteSrId = sessionStorage.getItem('instituteId');

    const statusOptions = [
        { value: "completed", label: "Completed" },
        { value: "pending", label: "Pending" }
    ];

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        fetchData();
    }, [currentPage]);

    const fetchInitialData = async () => {
        try {
            // Fetch Degrees
            try {
                const degreeUrl = new URL(`${API_BASE_URL}/api/degree-titles`);
                degreeUrl.searchParams.append("institute_id", instituteSrId);

                const degreeRes = await fetch(degreeUrl, {
                    method: 'GET',
                    headers: {
                        'Authorization': API_TOKEN,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });

                const degreeData = await degreeRes.json();
                if (degreeData.status === true && degreeData.data) {
                    setDegreeOptions(degreeData.data.map(d => ({ value: d, label: d })));
                }
            } catch (error) {
                console.error('Error fetching degrees:', error);
            }

            // Populate Studying Year Options from stream_data
            if (stream_data.studying_years) {
                setStudyingYearOptions(stream_data.studying_years.map(y => ({ value: y, label: y })));
            }

            // Fetch Programs
            const programRes = await fetch(`${API_BASE_URL}/api/institutes/${instituteSrId}/programs`, {
                headers: {
                    'Authorization': API_TOKEN,
                    'Accept': 'application/json'
                }
            });
            const programData = await programRes.json();
            if (programData.status && programData.data) {
                setProgramOptions(programData.data.map(p => ({ value: p.id, label: p.program_title })));
            }

            // Fetch Students (for Email search)
            const studentRes = await fetch(`${API_BASE_URL}/api/students-by-institute`, {
                method: 'POST',
                headers: {
                    'Authorization': API_TOKEN,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ institute_id: instituteSrId })
            });
            const studentData = await studentRes.json();
            if (studentData.status === "success" && studentData.data) {
                const students = studentData.data;

                // Student Options (Email Search) - Use Map to ensure uniqueness by email/id
                const uniqueStudents = new Map();
                students.forEach(s => {
                    if (!uniqueStudents.has(s.id)) {
                        uniqueStudents.set(s.id, {
                            value: s.id,
                            label: `${s.first_name} ${s.last_name} (${s.email})`,
                            email: s.email
                        });
                    }
                });
                setStudentOptions(Array.from(uniqueStudents.values()));
            }
        } catch (error) {
            console.error('Error fetching initial filter data:', error);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const payload = {
                institute_id: instituteSrId,
                page: currentPage,
                // per_page: itemsPerPage,
                // Filters
                program_id: programFilter?.value || null,
                student_id: selectedStudent?.value || null,
                degree_title: degreeFilter?.value || null,
                studying_year: studyingYearFilter?.value || null,
                completion_status: statusFilter?.value || null
            };

            const response = await fetch(`${API_BASE_URL}/api/student-program-progress`, {
                method: 'POST',
                headers: {
                    'Authorization': API_TOKEN,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (result.success && result.data && result.data.data) {
                const mappedData = result.data.data.map((item, index) => ({
                    uniqueKey: `${item.student_id}_${index}`,
                    id: item.student_id,
                    name: `${item.first_name} ${item.last_name}`,
                    email: item.email,
                    phone: item.contact_number,
                    institute: item.institute_name,
                    program: item.program_title,
                    total: item.total_modules,
                    completed: parseInt(item.completed_modules),
                    pending: parseInt(item.pending_modules),
                    status: item.program_status,
                    degree: item.degree_title,
                    studying_year: item.studying_year
                }));

                setData(mappedData);
                setPagination({
                    current_page: result.data.current_page,
                    last_page: result.data.last_page,
                    total: result.data.total,
                    from: result.data.from,
                    to: result.data.to
                });
            } else {
                setData([]);
                setPagination({ current_page: 1, last_page: 1, total: 0, from: 0, to: 0 });
            }
        } catch (error) {
            console.error('Error fetching report data:', error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    // Custom Styles for React Select
    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            backgroundColor: '#f8f9fa', // bg-light
            border: '0',
            boxShadow: 'none',
            minHeight: '42px'
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999
        })
    };

    const handleFilter = () => {
        if (currentPage === 1) {
            fetchData();
        } else {
            setCurrentPage(1);
        }
    };

    const handleMobileFilter = () => {
        handleFilter();
        setShowFilterCanvas(false);
    };

    const handleReset = () => {
        setSelectedStudent(null);
        setProgramFilter(null);
        setDegreeFilter(null);
        setStudyingYearFilter(null);
        setStatusFilter(null);

        setLoading(true);
        fetch(`${API_BASE_URL}/api/student-program-progress`, {
            method: 'POST',
            headers: { 'Authorization': API_TOKEN, 'Accept': 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ institute_id: instituteSrId, page: 1 })
        }).then(res => res.json()).then(result => {
            if (result.success && result.data && result.data.data) {
                const mappedData = result.data.data.map((item, index) => ({
                    uniqueKey: `${item.student_id}_${index}`,
                    id: item.student_id,
                    name: `${item.first_name} ${item.last_name}`,
                    email: item.email,
                    phone: item.contact_number,
                    institute: item.institute_name,
                    program: item.program_title,
                    total: item.total_modules,
                    completed: parseInt(item.completed_modules),
                    pending: parseInt(item.pending_modules),
                    status: item.program_status,
                    degree: item.degree_title,
                    studying_year: item.studying_year
                }));
                setData(mappedData);
                setPagination({
                    current_page: result.data.current_page,
                    last_page: result.data.last_page,
                    total: result.data.total,
                    from: result.data.from,
                    to: result.data.to
                });
            }
            setLoading(false);
        }).catch(() => setLoading(false));

        setCurrentPage(1);
    };

    const handleExport = async () => {
        try {
            const payload = {
                institute_id: instituteSrId,
                program_id: programFilter?.value || null,
                student_id: selectedStudent?.value || null,
                degree_title: degreeFilter?.value || null,
                studying_year: studyingYearFilter?.value || null,
                completion_status: statusFilter?.value || null
            };

            const response = await fetch(`${API_BASE_URL}/api/student-program-progress/export`, {
                method: 'POST',
                headers: {
                    'Authorization': API_TOKEN,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const contentType = response.headers.get("content-type");

            if (contentType && contentType.includes("application/json")) {
                const result = await response.json();
                if (result.status && result.data) {
                    window.open(result.data, '_blank');
                } else {
                    console.error("Export failed:", result.message || "Unknown error");
                }
            } else {
                // Assume it's a file blob
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;

                const date = new Date();
                const formattedDate = `${date.getDate()}${date.toLocaleString('default', { month: 'short' })}${date.getFullYear()}`;

                link.setAttribute('download', `StudentProgramReport_${formattedDate}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }

        } catch (error) {
            console.error('Error exporting data:', error);
        }
    };

    // Table Columns
    const columns = [
        { label: '#', key: 'id' },
        {
            label: 'Student',
            key: 'student',
            render: (_, row) => (
                <div style={{ lineHeight: '1.4' }}>
                    <div className="fw-bold text-dark">{row.name}</div>
                    <div className="text-secondary small">{row.email}</div>
                    <div className="text-secondary small">{row.phone}</div>
                    <div className="text-danger small">{row.institute}</div>
                </div>
            )
        },
        { label: 'Program', key: 'program', render: (text) => <span className="text-primary">{text}</span> },
        { label: 'Total Courses', key: 'total', render: (val) => <div className="text-center">{val}</div> },
        { label: 'Completed', key: 'completed', render: (val) => <div className="text-center text-success">{val}</div> },
        { label: 'Pending', key: 'pending', render: (val) => <div className="text-center text-warning">{val}</div> },
        {
            label: 'Program completion Status',
            key: 'status',
            render: (status) => (
                <span className={`badge ${status === 'Completed' ? 'bg-success' : 'bg-warning text-dark'} px-3 py-2 rounded-pill`}>
                    {status}
                </span>
            )
        }
    ];

    return (
        <div>
            <div className="col-12">
                <PageHeader title="Student Program Report"
                    breadcrumbs={[
                        { label: "Dashboard", path: "/institute-dashboard" },
                        { label: "Student Program Report", active: true }
                    ]}
                />
            </div>
            <div className="student-program-report-page">

                {/* Mobile Filter & Export Buttons (Visible only on lg and below) */}
                <div className="d-block d-lg-none mb-4">
                    <div className="card p-3 border-0 shadow-sm">
                        <Button variant="primary" className="w-100 mb-2 py-2" onClick={() => setShowFilterCanvas(true)}>
                            <i className="bi bi-funnel me-2"></i> Filters
                        </Button>
                        <Button variant="info" className="w-100 text-white py-2" onClick={handleExport} style={{ backgroundColor: '#17a2b8' }}>
                            <i className="bi bi-file-earmark-spreadsheet me-2"></i> Export Excel
                        </Button>
                    </div>
                </div>

                {/* Desktop Filters Row (Hidden on mobile) */}
                <div className='card p-4 d-none d-lg-block'>
                    <div className="row g-3 mb-4">
                        <div className="col-xl-3 col-lg-4 col-md-6 col-sm-6 col-12">
                            <label className="form-label mb-1">Student Email/Name</label>
                            <Select
                                options={studentOptions}
                                value={selectedStudent}
                                onChange={setSelectedStudent}
                                placeholder="Select Student"
                                styles={customSelectStyles}
                                isClearable
                                isSearchable
                                components={{ IndicatorSeparator: () => null }}
                            />
                        </div>
                        <div className="col-xl-3 col-lg-4 col-md-6 col-sm-6 col-12">
                            <label className="form-label mb-1">Program</label>
                            <Select
                                options={programOptions}
                                value={programFilter}
                                onChange={setProgramFilter}
                                placeholder="Select Program"
                                styles={customSelectStyles}
                                isClearable
                                components={{ IndicatorSeparator: () => null }}
                            />
                        </div>
                        <div className="col-xl-3 col-lg-4 col-md-6 col-sm-6 col-12">
                            <label className="form-label mb-1">Degree</label>
                            <Select
                                options={degreeOptions}
                                value={degreeFilter}
                                onChange={setDegreeFilter}
                                placeholder="Select Degree"
                                styles={customSelectStyles}
                                isClearable
                                components={{ IndicatorSeparator: () => null }}
                            />
                        </div>
                        <div className="col-xl-3 col-lg-4 col-md-6 col-sm-6 col-12">
                            <label className="form-label mb-1">Studying Year</label>
                            <Select
                                options={studyingYearOptions}
                                value={studyingYearFilter}
                                onChange={setStudyingYearFilter}
                                placeholder="Select Year"
                                styles={customSelectStyles}
                                isClearable
                                components={{ IndicatorSeparator: () => null }}
                            />
                        </div>
                        <div className="col-xl-3 col-lg-4 col-md-6 col-sm-6 col-12">
                            <label className="form-label mb-1">Completion Status</label>
                            <Select
                                options={statusOptions}
                                value={statusFilter}
                                onChange={setStatusFilter}
                                placeholder="Completion Status"
                                styles={customSelectStyles}
                                isClearable
                                components={{ IndicatorSeparator: () => null }}
                            />
                        </div>
                        <div className="col-xl-9 col-lg-4 col-md-6 col-sm-6 col-12 align-content-end text-end">
                            <div className="d-flex gap-2 justify-content-end">
                                <button className="btn btn-primary" onClick={handleFilter}>
                                    Filter
                                </button>
                                <button className="btn btn-light" onClick={handleReset}>
                                    Reset
                                </button>
                                <button className="btn btn-info text-white" onClick={handleExport} style={{ backgroundColor: '#17a2b8' }}>
                                    Export Excel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mobile Filter Offcanvas */}
                <Offcanvas show={showFilterCanvas} onHide={() => setShowFilterCanvas(false)} placement="end">
                    <Offcanvas.Header closeButton>
                        <Offcanvas.Title>Filter Report</Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body>
                        <div className="d-flex flex-column gap-3">
                            <div>
                                <label className="form-label mb-1">Student Email/Name</label>
                                <Select
                                    options={studentOptions}
                                    value={selectedStudent}
                                    onChange={setSelectedStudent}
                                    placeholder="Select Student"
                                    styles={customSelectStyles}
                                    isClearable
                                    isSearchable
                                />
                            </div>
                            <div>
                                <label className="form-label mb-1">Program</label>
                                <Select
                                    options={programOptions}
                                    value={programFilter}
                                    onChange={setProgramFilter}
                                    placeholder="Select Program"
                                    styles={customSelectStyles}
                                    isClearable
                                />
                            </div>
                            <div>
                                <label className="form-label mb-1">Degree</label>
                                <Select
                                    options={degreeOptions}
                                    value={degreeFilter}
                                    onChange={setDegreeFilter}
                                    placeholder="Select Degree"
                                    styles={customSelectStyles}
                                    isClearable
                                />
                            </div>
                            <div>
                                <label className="form-label mb-1">Studying Year</label>
                                <Select
                                    options={studyingYearOptions}
                                    value={studyingYearFilter}
                                    onChange={setStudyingYearFilter}
                                    placeholder="Select Year"
                                    styles={customSelectStyles}
                                    isClearable
                                />
                            </div>
                            <div>
                                <label className="form-label mb-1">Completion Status</label>
                                <Select
                                    options={statusOptions}
                                    value={statusFilter}
                                    onChange={setStatusFilter}
                                    placeholder="Completion Status"
                                    styles={customSelectStyles}
                                    isClearable
                                />
                            </div>
                            <div className="d-flex gap-2 mt-3">
                                <Button variant="primary" className="flex-grow-1" onClick={handleMobileFilter}>
                                    Apply Filters
                                </Button>
                                <Button variant="light" className="border" onClick={handleReset}>
                                    Reset
                                </Button>
                            </div>
                        </div>
                    </Offcanvas.Body>
                </Offcanvas>

                {/* Desktop Table View (Hidden on Mobile) */}
                <div className="card border-0 shadow-sm rounded-3 overflow-hidden d-none d-lg-block">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="bg-light text-dark">
                                <tr>
                                    <th className="py-3 ps-4 text-nowrap border-0">Student Name</th>
                                    <th className='py-3 text-nowrap border-0'>Degree</th>
                                    <th className="py-3 text-nowrap border-0">Program</th>
                                    <th className="py-3 text-center text-nowrap border-0">Total Modules</th>
                                    <th className="py-3 text-center text-nowrap border-0">Completed</th>
                                    <th className="py-3 text-center text-nowrap border-0">Pending</th>
                                    <th className="py-3 text-center text-nowrap border-0">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="text-muted mt-2 mb-0">Loading student data...</p>
                                        </td>
                                    </tr>
                                ) : data.length > 0 ? (
                                    data.map((row) => (
                                        <tr key={row.uniqueKey}>
                                            <td className="ps-4">
                                                <div className="d-flex flex-column py-1">
                                                    <span className="fw-bold text-dark">{row.name}</span>
                                                    <div className="d-flex flex-column small text-muted mt-1">
                                                        <span className="d-flex align-items-center gap-1"><i className="bi bi-envelope"></i> {row.email}</span>
                                                        <span className="d-flex align-items-center gap-1"><i className="bi bi-telephone"></i> {row.phone}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <span className="fw-medium text-dark">{row.degree}</span>
                                                    <span className="small text-muted">{row.studying_year}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="fw-medium text-primary">{row.program}</span>
                                            </td>
                                            <td className="text-center">
                                                <span className="text-dark">{row.total}</span>
                                            </td>
                                            <td className="text-center">
                                                {row.completed > 0 ? (
                                                    <span className="text-success fw-bold"><i className="bi bi-check-circle-fill me-1"></i>{row.completed}</span>
                                                ) : (
                                                    <span className="text-muted sub-text">-</span>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                {row.pending > 0 ? (
                                                    <span className="text-danger fw-bold">{row.pending} <span className="small text-muted fw-normal">left</span></span>
                                                ) : (
                                                    <span className="text-muted">0 left</span>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <span className={`badge ${row.status === 'Completed' ? 'bg-success-subtle text-white border border-success' : 'bg-warning-subtle text-warning-emphasis border border-warning'} px-3 py-2 rounded-pill`}>
                                                    {row.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5">
                                            <div className="text-muted">
                                                <i className="bi bi-inbox fs-1 d-block mb-3"></i>
                                                <p className="mb-0">No records found matching your filters.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Mobile Card View (Visible only on Mobile) */}
                <div className="d-block d-lg-none">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="text-muted mt-2 mb-0">Loading student data...</p>
                        </div>
                    ) : data.length > 0 ? (
                        data.map((row) => (
                            <div className="card border-0 shadow-sm mb-3" key={row.uniqueKey}>
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div>
                                            <h6 className="fw-bold text-dark mb-1">{row.name}</h6>
                                            <small className="text-muted d-block" style={{ fontSize: '0.85rem' }}>{row.email}</small>
                                        </div>
                                        <span className={`badge ${row.status === 'Completed' ? 'bg-success-subtle text-white border border-success' : 'bg-warning-subtle text-warning-emphasis border border-warning'} rounded-pill`}>
                                            {row.status}
                                        </span>
                                    </div>

                                    <div className="mb-3">
                                        <div className="small text-primary mb-1">
                                            <i className="bi bi-book me-1"></i> {row.degree} ({row.studying_year})
                                        </div>
                                        <div className="small text-muted mb-1">
                                            <i className="bi bi-telephone me-1"></i> {row.phone}
                                        </div>
                                    </div>

                                    <div className="mb-3 p-2 bg-light rounded-2 border-start border-4 border-primary">
                                        <small className="text-uppercase text-muted" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Program</small>
                                        <div className="fw-medium text-primary text-break">{row.program}</div>
                                    </div>

                                    <div className="row g-2 text-center">
                                        <div className="col-4">
                                            <div className="p-2 border rounded bg-white h-100">
                                                <small className="d-block text-muted mb-1" style={{ fontSize: '0.75rem' }}>Total</small>
                                                <span className="fw-bold">{row.total}</span>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="p-2 border rounded bg-white h-100">
                                                <small className="d-block text-muted mb-1" style={{ fontSize: '0.75rem' }}>Done</small>
                                                <span className="fw-bold text-success">{row.completed}</span>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="p-2 border rounded bg-white h-100">
                                                <small className="d-block text-muted mb-1" style={{ fontSize: '0.75rem' }}>Left</small>
                                                <span className="fw-bold text-danger">{row.pending}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="card border-0 shadow-sm p-4 text-center">
                            <div className="text-muted">
                                <i className="bi bi-inbox fs-1 d-block mb-3"></i>
                                <p className="mb-0">No records found matching your filters.</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {pagination.last_page > 1 && (
                    <div className="mt-3 d-flex justify-content-end">
                        <PaginationControls
                            currentPage={currentPage}
                            lastPage={pagination.last_page}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                )}

                <div className="text-secondary small mt-2">
                    Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total} results
                </div>
            </div>
        </div>
    );
};

export default StudentProgramReport;
